// hooks/useStake.js

/* eslint-env es2020 */
import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice, readContract } from 'wagmi/actions';
import { parseUnits, formatUnits } from 'viem';
import { newPool, claimRequestContract } from '../utils/contract';
import { useOwner } from './useOwner';
import { useBondSize } from './useBondSize';

// Safe formatting function to handle large BigInt values
function safeFormatUnits(value, decimals = 18) {
  try {
    if (value == null) return '0';

    // Normalize input
    let raw = value;
    if (Array.isArray(raw)) raw = raw[0];
    else if (typeof raw === 'object' && raw !== null && typeof raw !== 'bigint') {
      const vals = Object.values(raw);
      if (vals.length > 0) {
        raw = vals.find(v => typeof v === 'bigint' || /^[0-9]+n?$/.test(v?.toString?.() || '')) ?? raw;
      }
    }

    // Convert to BigInt
    let bigVal;
    if (typeof raw === 'bigint') {
      bigVal = raw;
    } else {
      let strVal = String(raw).trim();
      if (strVal.endsWith('n')) strVal = strVal.slice(0, -1);
      strVal = strVal.replace(/[^\d]/g, '');
      if (strVal === '' || strVal === '0') return '0';
      bigVal = BigInt(strVal);
    }

    // Validate decimals
    if (decimals < 0 || decimals > 77) {
      console.warn(`Invalid decimals value: ${decimals}, using 18`);
      decimals = 18;
    }

    // Create divisor safely using loop to avoid BigInt size issues
    let divisor = 1n;
    for (let i = 0; i < decimals; i++) {
      divisor *= 10n;
    }
    
    const integerPart = bigVal / divisor;
    const fractionalPart = bigVal % divisor;
    
    const intStr = integerPart.toString();
    let fracStr = fractionalPart.toString();
    
    // Pad fractional part manually
    const paddingNeeded = decimals - fracStr.length;
    if (paddingNeeded > 0 && paddingNeeded < 100) {
      let padding = '';
      for (let i = 0; i < paddingNeeded; i++) {
        padding += '0';
      }
      fracStr = padding + fracStr;
    }
    
    // Trim to reasonable precision
    if (fracStr.length > 18) {
      fracStr = fracStr.substring(0, 18);
    }
    
    // Trim trailing zeros
    fracStr = fracStr.replace(/0+$/, '');
    
    // Combine
    return fracStr ? `${intStr}.${fracStr}` : intStr;
  } catch (err) {
    console.error('safeFormatUnits error:', err, value);
    return '0';
  }
}

export const useStake = () => {
    const [isStaking, setIsStaking] = useState(false);
    const [isUnstaking, setIsUnstaking] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isClaimingAndRestaking, setIsClaimingAndRestaking] = useState(false);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isClaimingUnstaked, setIsClaimingUnstaked] = useState(false);
    const config = useConfig();
    const { owner } = useOwner();
    const { bondSize } = useBondSize();

    /** ---------- READ FUNCTIONS ---------- **/
    const getTotalStaked = useCallback(
        async (contractAddress, decimals = 18) => {
            try {
                setIsLoading(true);
                const stakingContract = newPool;

                const result = await readContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'totalStaked',
                });

                const totalStakedValue = Array.isArray(result) ? result[0] : result;
                const totalStakedETH = safeFormatUnits(totalStakedValue, decimals);
                const totalStakedFormatted = parseFloat(totalStakedETH).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6
                });

                return {
                    totalStakedWei: totalStakedValue?.toString(),
                    totalStakedETH,
                    totalStakedFormatted,
                    totalStakedNumber: parseFloat(totalStakedETH)
                };
            } catch (error) {
                console.error("Error getting total staked:", error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [config]
    );

    const getRewards = useCallback(
        async (contractAddress, userAddress, decimals = 18) => {
            try {
                setIsLoading(true);
                const stakingContract = newPool;

                const result = await readContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'getReward',
                    args: [userAddress],
                });

                // console.log(result, 'getRewardsresult');

                const rewardsValue = Array.isArray(result) ? result[0] : result;
                const rewardsETH = safeFormatUnits(rewardsValue, decimals);
                const rewardsFormatted = parseFloat(rewardsETH).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6
                });

                return {
                    rewardsWei: rewardsValue?.toString(),
                    rewardsETH,
                    rewardsFormatted,
                    rewardsNumber: parseFloat(rewardsETH)
                };
            } catch (error) {
                console.error("Error getting rewards:", error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [config]
    );

    const stakerRewardPerTokenPaid = useCallback(
        async (contractAddress, userAddress, decimals = 18) => {
            try {
                setIsLoading(true);
                const stakingContract = newPool;

                const result = await readContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'stakerRewardPerTokenPaid',
                    args: [userAddress],
                });

                const rewardValue = Array.isArray(result) ? result[0] : result;
                const rewardETH = safeFormatUnits(rewardValue, decimals);
                const rewardFormatted = parseFloat(rewardETH).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6
                });

                return {
                    rewardPerTokenWei: rewardValue?.toString(),
                    rewardPerTokenETH: rewardETH,
                    rewardPerTokenFormatted: rewardFormatted,
                    rewardPerTokenNumber: parseFloat(rewardETH),
                };
            } catch (error) {
                console.error("Error getting stakerRewardPerTokenPaid:", error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [config]
    );

    /** ---------- GAS ESTIMATION ---------- **/
    const estimateGasFor = useCallback(
        async (contractAddress, functionName, args = []) => {
            try {
                setIsEstimatingGas(true);
                const stakingContract = newPool;

                const estimatedGas = await estimateGas(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName,
                    args,
                });

                const gasPrice = await getGasPrice(config);
                const totalGasCost = estimatedGas * gasPrice;
                const totalGasCostETH = formatUnits(totalGasCost, 18);

                return {
                    estimatedGas: estimatedGas?.toString(),
                    gasPrice: gasPrice?.toString(),
                    totalGasCostWei: totalGasCost?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH
                };
            } catch (error) {
                console.error(`Error estimating gas for ${functionName}:`, error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    /** ---------- STAKE ---------- **/
    const stake = useCallback(
        async (contractAddress, amount, decimals = 18) => {
            try {
                setIsStaking(true);
                const amountWei = parseUnits(amount?.toString(), decimals);
                const stakingContract = newPool;

                // console.log("aount staked", {amount, amountWei})

                const hash = await writeContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'stake',
                    args: [amountWei],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });
                return { receipt, hash };
            } catch (error) {
                console.error("Error staking:", error);
                throw error;
            } finally {
                setIsStaking(false);
            }
        },
        [config]
    );

    /** ---------- UNSTAKE ---------- **/
    const unstake = useCallback(
        async (contractAddress, amount, decimals = 18) => {
            try {
                setIsUnstaking(true);
                // console.log(amount, 'unstake amount');
                // console.log(decimals, 'unstake decimals');
                const amountWei = parseUnits(amount?.toString(), decimals);
                console.log("amount, amountinWei", {amount, amountWei});
                // console.log(amountWei, 'unstake amountWei');
                const stakingContract = newPool;
                // console.log(amountWei, 'unstake amountWei');

                const hash = await writeContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'requestUnstake',
                    args: [amountWei],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });
                return { receipt, hash };
            } catch (error) {
                console.error("Error unstaking:", error);
                throw error;
            } finally {
                setIsUnstaking(false);
            }
        },
        [config]
    );

    /** ---------- GET AVAILABLE STAKES ---------- **/
    const getAvailableStakes = useCallback(
        async (contractAddress, userAddress, decimals = 18) => {
            try {
                const stakingContract = newPool;
    
                // 1️⃣ Fetch stake data from contract
                const availableStakes = await readContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'stakes',
                    args: [userAddress],
                });
    
                // console.log(availableStakes, "Available Stakes");
                
                // 2️⃣ Handle struct return (array or object)
                const amount = availableStakes?.[0] ?? availableStakes.amount;
                const restakeAmount = availableStakes?.[1] ?? availableStakes.restakeAmount;
                const claimTime = availableStakes?.[2] ?? availableStakes.claimTime;
    
                // 3️⃣ Format values using safe formatter
                const formattedAmount = safeFormatUnits(amount || 0, decimals);
                const formattedRestakeAmount = safeFormatUnits(restakeAmount || 0, decimals);
    
                // 4️⃣ Convert claimTime (seconds) to readable countdown
                let claimCountdown = "00D:00H:00M";
                if (claimTime && Number(claimTime) > 0) {
                    const now = Math.floor(Date.now() / 1000);
                    let diff = Number(claimTime) - now;
                    if (diff < 0) diff = 0;
    
                    const days = Math.floor(diff / (60 * 60 * 24));
                    const hours = Math.floor((diff % (60 * 60 * 24)) / (60 * 60));
                    const minutes = Math.floor((diff % (60 * 60)) / 60);
    
                    const formatUnit = (num) => String(num).padStart(2, "0");
                    claimCountdown = `${formatUnit(days)}D:${formatUnit(hours)}H:${formatUnit(minutes)}M`;
                }
    
                // 5️⃣ Fetch owner + bond data
                const poolOwner = await owner(contractAddress);
                const cleanOwner = poolOwner?.replace(/\./g, "");
                const bondAmount = formatUnits(await bondSize(contractAddress),decimals);
    
                // 6️⃣ Adjust available amount if current user is owner
                const amountNumber = parseFloat(formattedAmount);
                const restakeAmountNumber = parseFloat(formattedRestakeAmount);
                const bondValue = Number(bondAmount || 0);
    
                const isOwner = cleanOwner?.toLowerCase() === userAddress?.toLowerCase();
                const adjustedAmount = isOwner ? Math.max(0, amountNumber - bondValue) : amountNumber;
                // console.log("BOND AMOUNT || amountNumber", {amountFromStakesCall: amountNumber, bondValue, amountAfterAdjustment:adjustedAmount});

                // console.log(amount, "Amount");
    
                return {
                    amountWei: amount?.toString() || "0",
                    restakeAmountWei: restakeAmount?.toString() || "0",
                    claimTime: claimTime?.toString() || "0",
    
                    amountETH: adjustedAmount.toString(),
                    restakeAmountETH: formattedRestakeAmount,
                    claimCountdown,
    
                    amountNumber: adjustedAmount,
                    restakeAmountNumber,
                };
            } catch (error) {
                console.error("Error fetching available stakes:", error);
                throw error;
            }
        },
        [config, owner, bondSize]
    );

    /** ---------- CLAIM REWARDS ---------- **/
    const claimRewards = useCallback(
        async (contractAddress) => {
            try {
                setIsClaiming(true);
                const stakingContract = newPool;

                const hash = await writeContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'claim',
                    args: [],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });
                return { receipt, hash };
            } catch (error) {
                console.error("Error claiming rewards:", error);
                throw error;
            } finally {
                setIsClaiming(false);
            }
        },
        [config]
    );

    /** ---------- CLAIM AND RESTAKE ---------- **/
    const claimAndRestake = useCallback(
        async (contractAddress) => {
            try {
                setIsClaimingAndRestaking(true);
                const stakingContract = newPool;

                const hash = await writeContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'claimAndRestake',
                    args: [],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });
                return { receipt, hash };
            } catch (error) {
                console.error("Error in claimAndRestake:", error);
                throw error;
            } finally {
                setIsClaimingAndRestaking(false);
            }
        },
        [config]
    );

    /** ---------- CLAIM UNSTAKED ---------- **/
    const claimUnstaked = useCallback(
        async (contractAddress, index) => {
            // console.log(index, 'claimUnstaked index');
            try {
                setIsClaimingUnstaked(true);
                const stakingContract = claimRequestContract;

                const hash = await writeContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'claimRequest',
                    args: [index],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });
                return { receipt, hash };
            } catch (error) {
                console.error('Error claiming unstaked:', error);
                throw error;
            } finally {
                setIsClaimingUnstaked(false);
            }
        },
        [config]
    );

    /** ---------- ESTIMATE GAS FOR CLAIM UNSTAKED ---------- **/
    const estimateClaimUnstakedGas = useCallback(
        async (contractAddress, index) => {
            try {
                setIsEstimatingGas(true);
                const stakingContract = newPool;

                const estimatedGas = await estimateGas(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'claimUnstaked',
                    args: [index],
                });

                const gasPrice = await getGasPrice(config);
                const totalGasCost = estimatedGas * gasPrice;
                const totalGasCostETH = formatUnits(totalGasCost, 18);

                return {
                    estimatedGas: estimatedGas?.toString(),
                    gasPrice: gasPrice?.toString(),
                    totalGasCostWei: totalGasCost?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH,
                };
            } catch (error) {
                console.error('Error estimating gas for claimUnstaked:', error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    return {
        // States
        isStaking,
        isUnstaking,
        isClaiming,
        isClaimingAndRestaking,
        isEstimatingGas,
        isLoading,
        isClaimingUnstaked,

        // Read Functions
        getTotalStaked,
        getRewards,
        getAvailableStakes,
        stakerRewardPerTokenPaid,

        // Actions
        stake,
        unstake,
        claimRewards,
        claimAndRestake,
        claimUnstaked,

        // Gas Estimation
        estimateStakeGas: (contractAddress, amount, decimals = 18) =>
            estimateGasFor(contractAddress, 'stake', [parseUnits(amount?.toString(), decimals)]),
        estimateUnstakeGas: (contractAddress, amount, decimals = 18) =>
            estimateGasFor(contractAddress, 'unstake', [parseUnits(amount?.toString(), decimals)]),
        estimateClaimRewardsGas: (contractAddress) =>
            estimateGasFor(contractAddress, 'claim', []),
        estimateClaimAndRestakeGas: (contractAddress) =>
            estimateGasFor(contractAddress, 'claimAndRestake', []),
        estimateClaimUnstakedGas,
    };
};